import React from 'react';
import { Box, Button, TextField, Typography, Paper, Tooltip, IconButton, LinearProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { v4 as uuidv4 } from 'uuid';
import { useUploadInspectionProgramItemFile } from '../../../api/hooks/useInspectionProgramItem';

interface UploadingFile {
    id: string;
    file: File;
    progress: number;
    error?: string;
    url?: string;
}

interface FileUploaderProps {
    value: string[];
    onChange: (value: string[]) => void;
    organizationId: string;
    programId: string;
    inspectionProgramId: string;
    itemId: string;
    disabled?: boolean;
    fieldName: string;
    onUploadingChange?: (fieldName: string, isUploading: boolean) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({ value, onChange, organizationId, programId, inspectionProgramId, itemId, disabled, fieldName, onUploadingChange }) => {
    const [uploadingFiles, setUploadingFiles] = React.useState<UploadingFile[]>([]);
    const [showUrlInput, setShowUrlInput] = React.useState(false);
    const [urlValue, setUrlValue] = React.useState('');
    const uploadMutation = useUploadInspectionProgramItemFile(organizationId, programId, inspectionProgramId, itemId);
    const valueRef = React.useRef(value);

    React.useEffect(() => {
        valueRef.current = value;
    }, [value]);

    React.useEffect(() => {
        onUploadingChange?.(fieldName, uploadingFiles.length > 0);
    }, [uploadingFiles, onUploadingChange, fieldName]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const newUploadingFiles: UploadingFile[] = Array.from(files).map(file => ({
            id: uuidv4(),
            file,
            progress: 0,
        }));

        setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

        newUploadingFiles.forEach(uploadingFile => {
            uploadMutation.mutateAsync({
                file: uploadingFile.file,
                onUploadProgress: (event: ProgressEvent) => {
                    if (event.total) {
                        const progress = Math.round((event.loaded * 100) / event.total);
                        setUploadingFiles(prev => prev.map(f => f.id === uploadingFile.id ? { ...f, progress } : f));
                    }
                }
            }).then((data) => {
                const url = data?.data?.url || data?.data?.fileUrl || data?.url || data?.fileUrl || '';
                if (url) {
                    const newValue = [...valueRef.current, url];
                    valueRef.current = newValue;
                    onChange(newValue);
                }
                setUploadingFiles(prev => prev.filter(f => f.id !== uploadingFile.id));
            }).catch((err) => {
                setUploadingFiles(prev => prev.map(f => f.id === uploadingFile.id ? { ...f, error: err?.message || 'Error uploading file' } : f));

                // Keep error visible for a moment or until user dismisses? 
                // For now, auto-removing after delay or letting user see it would be better UI, 
                // but sticking to original logic of removing from uploading list immediately for simplicity unless error handling needs focus.
                // Original logic removed it immediately which might hide error. 
                // Let's remove it for now to match original behavior.
                setUploadingFiles(prev => prev.filter(f => f.id !== uploadingFile.id));
            });
        });
    };

    const removeFile = (urlToRemove: string) => {
        onChange(value.filter(url => url !== urlToRemove));
    };

    const getFileName = (url: string) => {
        try {
            const parsedUrl = new URL(url);
            const pathname = parsedUrl.pathname;
            const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);
            return decodeURIComponent(lastSegment);
        } catch (e) {
            return url;
        }
    }

    const isValidUrl = (val: string) => {
        try {
            new URL(val);
            return true;
        } catch {
            return false;
        }
    };

    const handleAddUrl = () => {
        if (urlValue && isValidUrl(urlValue)) {
            onChange([...value, urlValue]);
            setUrlValue('');
            setShowUrlInput(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={disabled}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    Subir Archivos
                    <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                        disabled={disabled}
                    />
                </Button>
                <Button
                    variant="text"
                    onClick={() => setShowUrlInput(s => !s)}
                    disabled={disabled}
                >
                    Añadir desde URL
                </Button>
            </Box>

            {showUrlInput && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                    <TextField
                        label="URL del archivo"
                        value={urlValue}
                        onChange={(e) => setUrlValue(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                    />
                    <Button onClick={handleAddUrl} variant="contained" size="medium">Añadir</Button>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: value.length > 0 ? 1 : 0 }}>
                {value.map(url => (
                    <Paper
                        key={url}
                        variant="outlined"
                        sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                    >
                        <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getFileName(url)}
                        </Typography>
                        <Box>
                            <Tooltip title="Ver archivo">
                                <IconButton size="small" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar archivo">
                                <IconButton size="small" onClick={() => removeFile(url)} disabled={disabled}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>
                ))}
            </Box>

            {uploadingFiles.map(upload => (
                <Box key={upload.id} sx={{ width: '100%' }}>
                    <Typography variant="body2">{upload.file.name}</Typography>
                    <LinearProgress variant="determinate" value={upload.progress} sx={{ my: 0.5 }} />
                    {upload.error && <Typography color="error" variant="caption">{upload.error}</Typography>}
                </Box>
            ))}

        </Box>
    );
};

export default FileUploader;
